import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview TopMatchCard — LOCI · CLT For You hero card (FRE-772).
 *
 * 220px category-tinted gradient hero with a teal `#1 MATCH · {score}%`
 * pulse chip, Space Grotesk uppercase title, mono venue · time · distance
 * meta line, and a 3-row italic teal reason list. Whole card is a single
 * focusable link.
 *
 * Motion uses the existing global `animate-loci-pulse` / `animate-loci-breathe`
 * utilities so the project-wide `prefers-reduced-motion: reduce` rule
 * automatically disables the pulse for sensitive users.
 */
import React, { forwardRef } from 'react';
import { useTheme } from '../../../theme';
import { getCategoryGradient } from '../HappeningCard/categoryGradients';
const TEAL = '#4FC4C4';
const HERO_HEIGHT = 220;
const DefaultLink = React.forwardRef(function DefaultLink({ href, children, ...rest }, ref) {
    return (_jsx("a", { ref: ref, href: href, ...rest, children: children }));
});
function DefaultImage({ src, alt = '', ...rest }) {
    // Framework-agnostic UI package; web supplies a `next/image`
    // wrapper via `imageComponent`. This native fallback is intentional.
    return _jsx("img", { src: src, alt: alt, ...rest });
}
function clampScore(score) {
    if (!Number.isFinite(score))
        return 0;
    return Math.max(0, Math.min(100, Math.round(score)));
}
export const TopMatchCard = forwardRef(({ id: _id, title, imageUrl, venue, date, distance, category, matchScore, reasons, href, linkComponent: LinkComponent = DefaultLink, imageComponent: ImageComponent = DefaultImage, className, ...rest }, ref) => {
    const theme = useTheme();
    const gradient = getCategoryGradient(category);
    const score = clampScore(matchScore);
    const hasReasons = reasons.length > 0;
    const rootClasses = [
        'group relative block w-full overflow-hidden rounded-[14px]',
        'border border-border bg-surface text-text',
        'shadow-sm hover:shadow-lg motion-safe:transition-shadow motion-safe:duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className,
    ]
        .filter(Boolean)
        .join(' ');
    const heroStyle = {
        position: 'relative',
        height: HERO_HEIGHT,
        width: '100%',
        background: gradient,
        color: '#FFFFFF',
    };
    const overlayStyle = {
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.25) 60%, rgba(0,0,0,0.55) 100%)',
        pointerEvents: 'none',
    };
    const chipStyle = {
        position: 'absolute',
        top: 12,
        left: 12,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 10px',
        borderRadius: 9999,
        background: 'rgba(15, 20, 31, 0.55)',
        border: '1px solid rgba(79, 196, 196, 0.45)',
        color: TEAL,
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: 11,
        fontWeight: theme.typography.fontWeight.semibold,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        fontFeatureSettings: "'tnum' 1, 'zero' 1",
        boxShadow: '0 0 0 4px rgba(79, 196, 196, 0.18)',
    };
    const heroBodyStyle = {
        position: 'absolute',
        left: 16,
        right: 16,
        bottom: 14,
        color: '#FFFFFF',
    };
    const titleStyle = {
        margin: 0,
        marginBottom: 6,
        fontFamily: theme.typography.fontFamilyDisplay ?? theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeight.bold,
        fontSize: 20,
        lineHeight: 1.15,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    };
    const metaStyle = {
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: 12,
        letterSpacing: '0.04em',
        color: 'rgba(255, 255, 255, 0.92)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFeatureSettings: "'tnum' 1, 'zero' 1",
    };
    const reasonRowStyle = {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        color: TEAL,
        fontStyle: 'italic',
        fontFamily: theme.typography.fontFamily,
        fontSize: 13,
        lineHeight: 1.45,
    };
    const reasonDotStyle = {
        flex: '0 0 auto',
        marginTop: 6,
        width: 8,
        height: 8,
        borderRadius: '9999px',
        background: TEAL,
        boxShadow: '0 0 0 3px rgba(79, 196, 196, 0.24)',
    };
    const reasonTextStyle = {
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
    };
    return (_jsxs(LinkComponent, { ref: ref, href: href, "aria-label": `Top match recommendation: ${title}, ${score}% match`, className: rootClasses, "data-testid": "top-match-card", "data-category": category ?? 'unknown', ...rest, children: [_jsxs("div", { style: heroStyle, "data-testid": "top-match-card-hero", children: [imageUrl ? (_jsx(ImageComponent, { src: imageUrl, alt: "", "aria-hidden": "true", loading: "lazy", decoding: "async", className: "absolute inset-0 h-full w-full object-cover" })) : null, _jsx("span", { "aria-hidden": "true", style: overlayStyle }), _jsxs("span", { className: "motion-safe:animate-loci-pulse", style: chipStyle, "data-testid": "top-match-card-chip", children: ["#1 MATCH \u00B7 ", score, "%"] }), _jsxs("div", { style: heroBodyStyle, children: [_jsx("h3", { style: titleStyle, "data-testid": "top-match-card-title", children: title }), _jsxs("p", { style: metaStyle, "data-testid": "top-match-card-meta", children: [_jsx("span", { children: venue }), _jsx("span", { "aria-hidden": "true", children: " \u00B7 " }), _jsx("span", { children: date }), distance ? (_jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": "true", children: " \u00B7 " }), _jsx("span", { children: distance })] })) : null] })] })] }), hasReasons ? (_jsx("ul", { className: "flex flex-col gap-2 px-4 py-3", "data-testid": "top-match-card-reasons", "aria-label": "Why this is your top match", children: reasons.map((reason, index) => (_jsxs("li", { style: reasonRowStyle, "data-testid": "top-match-card-reason", children: [_jsx("span", { "aria-hidden": "true", className: "motion-safe:animate-loci-breathe", style: reasonDotStyle }), _jsx("span", { style: reasonTextStyle, children: reason })] }, `${index}-${reason}`))) })) : null] }));
});
TopMatchCard.displayName = 'TopMatchCard';
